import React from 'react'
import { useParams } from 'react-router-dom'
import {
  EditCompany,
  EditCompanyVariables,
  useAuth,
  AddressFormValues,
  useCompanyDetails,
  useClearanceSetup,
  useClearanceSetupForm,
  getClearanceSetupCompliant,
  ClearanceSetupFormValues,
  UserPermissions,
} from '@modmd/data'

import { useMutation } from '@apollo/client'
import { useToaster } from 'utils/useToaster'
import { useIsMinDevice } from 'utils/hooks/useMedia'
import { useCompanyForm } from 'utils/form/companyForm'
import DashboardPage from 'internal-portal/sharedComponents/DashboardPage'
import { Box } from 'components/Layout'
import Card from 'components/Card'
import { CreateEditCompany } from 'internal-portal/routes/components/CreateEditCompany'
import Button from 'components/Button'
import { Text } from 'components/Typography'
import Icon from 'components/Icon'
import { Switch } from 'components/Switch'
import { Dialog } from 'components/Dialog'
import { COLOR, DEVICE } from 'theme'
import { FormClearanceSetting } from 'components/ClearanceSetting'
import { FormCompanySettings } from 'components/CompanySettings'
import { Spinner } from 'components/Spinner'
import { EDIT_COMPANY } from './operations'
import { CompanyGroups } from './CompanyGroups'
import { CompanySessions } from './CompanySessions'
import { CompanyMembers } from './CompanyMembers'
import { CompanyPriceDatalist } from './CompanyPriceDatalist'
import { CompanyEvents } from './CompanyEvents'

interface Params {
  companyId: string
}

const CompanyDetail: React.VFC = () => {
  const [isDialogOpen, setIsDialogOpen] = React.useState<boolean>(false)
  const [isSettingsDialogOpen, setIsSettingsDialogOpen] = React.useState<boolean>(false)

  const { setToastMessage } = useToaster()

  const { hasPermission } = useAuth()
  const isTablet = useIsMinDevice(DEVICE.TABLET)
  const params = useParams<Params>()
  const [isEditing, setIsEditing] = React.useState(false)

  const [editCompany, editCompanyMutation] = useMutation<EditCompany, EditCompanyVariables>(
    EDIT_COMPANY,
    {
      onCompleted: () => {
        setToastMessage('Data successfully changed', 'success')
      },
    }
  )

  const companyForm = useCompanyForm({
    validationSchema: 'fullCompany',
    onSubmit: async ({
      name,
      Address,
      eventClearance,
      pcrOnly,
      insuranceOnly,
      automaticallyApproved,
    }) => {
      await editCompany({
        variables: {
          inputData: {
            id: params.companyId,
            name,
            ...(Address.city && Address.country && Address.zip && Address.street && Address.state
              ? {
                  Address: {
                    id: Address.id,
                    city: Address.city,
                    street: Address.street,
                    zip: Address.zip,
                    country: Address.country,
                    county: Address.county,
                    complement: Address.complement,
                    state: Address.state,
                    lat: Address.lat,
                    lon: Address.lon,
                  },
                }
              : {}),
            eventClearance,
            pcrOnly,
            insuranceOnly,
            automaticallyApproved,
          },
        },
      })
      setIsSettingsDialogOpen(false)
      setIsEditing(false)
    },
  })

  const companyQuery = useCompanyDetails({
    filter: { companyId: params.companyId },
    onCompleted: ({ company }) => {
      if (company) {
        void companyForm.setValues({
          name: company.name,
          Address: company.Address as AddressFormValues,
          Members: [],
          eventClearance: company.eventClearance,
          pcrOnly: company.pcrOnly,
          insuranceOnly: company.insuranceOnly,
          automaticallyApproved: company.automaticallyApproved,
        })
      }
    },
  })

  const submitCompany = (ignoreClearance = false) => {
    const { Address } = companyForm.values
    if (!(Address.city && Address.country && Address.zip && Address.street && Address.state)) {
      setToastMessage('Invalid address', 'danger')
      return
    }
    if (!ignoreClearance) {
      void companyForm.setValues({
        ...companyForm.values,
        eventClearance: !companyForm.values?.eventClearance,
      })
    }
    void companyForm.submitForm()
  }
  const clearanceSetupForm = useClearanceSetupForm({
    onSubmit: () => {},
  })

  const clearanceSetup = useClearanceSetup({
    companyId: params.companyId,
    onCompleted: ({ getClearanceSetup }) => {
      if (getClearanceSetup) {
        void clearanceSetupForm.setValues({
          ...clearanceSetupForm.values,
          ...getClearanceSetupCompliant(getClearanceSetup as ClearanceSetupFormValues),
        })
      }
    },
  })

  const submitClearanceSetup = () => {
    void clearanceSetupForm.submitForm()
    void submitCompany()
    void clearanceSetup.refetch()
  }

  const handleEditingToggle = React.useCallback(() => {
    setIsEditing((prevState) => !prevState)
  }, [])

  const handleSwitchChange = () => {
    const { Address } = companyForm.values
    if (!(Address.city && Address.country && Address.zip && Address.street && Address.state)) {
      setToastMessage('Invalid address', 'danger')
      return
    }
    if (!!companyForm.values.eventClearance && !!clearanceSetupForm.values) {
      submitCompany()
    } else {
      setIsDialogOpen(true)
    }
  }

  return (
    <DashboardPage
      Header={
        <Box
          display="flex"
          alignItems="center"
          justifyContent="space-between"
          height="100%"
          p="1rem"
        >
          <Text fontSize="xl" fontWeight={500}>
            {companyForm.values.name}
          </Text>
          <Box display="flex" alignItems="center" justifyContent="space-between" height="100%">
            {((!clearanceSetup?.data?.getClearanceSetup &&
              hasPermission([UserPermissions.SETUP_EVENT_CLEARANCE])) ||
              (!!clearanceSetup.data?.getClearanceSetup &&
                hasPermission([UserPermissions.TURN_ON_OFF_EVENT_CLEARANCE]))) && (
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Text mr="1.25rem" fontSize="m">
                  Allow FastPass
                </Text>
                {(companyQuery.loading || clearanceSetup.loading) && (
                  <Spinner size="small" color={COLOR.brand} />
                )}
                <Switch
                  size="small"
                  isChecked={
                    !!companyForm.values.eventClearance && !!clearanceSetup.data?.getClearanceSetup
                  }
                  isDisabled={companyQuery.loading || clearanceSetup.loading}
                  onChange={handleSwitchChange}
                />
              </Box>
            )}
            {hasPermission([UserPermissions.EDIT_COMPANY_SETTINGS]) && (
              <Box pl="2rem" display="flex" alignItems="center" justifyContent="space-between">
                <Button
                  size="small"
                  disabled={companyQuery.loading}
                  colorVariant="primary"
                  onClick={() => setIsSettingsDialogOpen(true)}
                  leftIcon={<Icon.SettingsOutline size="L" />}
                >
                  Settings
                </Button>
              </Box>
            )}
          </Box>
        </Box>
      }
    >
      <Box display="flex" flexWrap="wrap">
        <Box minWidth="300px" flex="1">
          <Card>
            <Card.Title
              rightContent={
                hasPermission([UserPermissions.EDIT_COMPANY]) && (
                  <Button
                    size="small"
                    disabled={editCompanyMutation.loading}
                    colorVariant={isEditing ? 'info' : 'primary'}
                    onClick={isEditing ? companyForm.submitForm : handleEditingToggle}
                  >
                    {isEditing ? <>Done</> : <Icon.Edit />}
                  </Button>
                )
              }
            >
              Details
            </Card.Title>
            <Card.Content>
              <CreateEditCompany
                isDisabled={!isEditing}
                companyForm={companyForm}
                resetOnCancel={!isEditing}
              />
            </Card.Content>
          </Card>
          <CompanyGroups companyQuery={companyQuery} />
        </Box>
        <Box
          flex="3"
          minWidth="280px"
          display="grid"
          gridGap="0.5rem"
          alignContent="start"
          pl={isTablet ? '1rem' : undefined}
          mt={!isTablet ? '1rem' : undefined}
        >
          <CompanyMembers />
          <CompanySessions />
          <CompanyEvents filter={{ companyId: params.companyId }} isClickable />
          <Card>
            <Card.Content pl={0} pr={0}>
              <CompanyPriceDatalist companyId={params.companyId} />
            </Card.Content>
          </Card>
        </Box>
      </Box>

      <Dialog
        isOpen={!!isDialogOpen}
        onDismiss={() => setIsDialogOpen(false)}
        variant="center"
        maxWidth="545px"
      >
        <Card.Title onDismiss={() => setIsDialogOpen(false)}>FastPass Settings</Card.Title>
        <FormClearanceSetting
          clearanceForm={clearanceSetupForm}
          onClose={() => setIsDialogOpen(false)}
          id={params.companyId}
          onSubmit={submitClearanceSetup}
        />
      </Dialog>

      <Dialog
        isOpen={!!isSettingsDialogOpen}
        onDismiss={() => setIsSettingsDialogOpen(false)}
        variant="center"
        maxWidth="545px"
      >
        <Card.Title onDismiss={() => setIsSettingsDialogOpen(false)}>Company Settings</Card.Title>
        <FormCompanySettings
          loading={editCompanyMutation.loading}
          companyForm={companyForm}
          onClose={() => setIsSettingsDialogOpen(false)}
          onSubmit={submitCompany}
        />
      </Dialog>
    </DashboardPage>
  )
}

export { CompanyDetail }
